import React, { useEffect, useState } from "react";
import { authorizedPost } from "../../Config/networkWithToken";
import configuration from "../../Config/configuration";
import { config, errorNotify, successNotify } from "../../utils/helper";
import { useQueryClient } from "@tanstack/react-query";
import { USERS_QUERY_KEY } from "../../Admin/Pages/Matrimony/useMatrimonyUsers";

const MentorDetails = ({ userData, Loading }) => {
  const [isMentorChecked, setIsMentorChecked] = useState(false);
  const [isCourseChecked, setIsCourseChecked] = useState(false);

  const queryClient = useQueryClient();
  const localStorageUserData = JSON.parse(localStorage.getItem("userData"));
  const adminId = localStorageUserData?.adminId || "";
  const role = localStorageUserData?.role;
  const canManage = role === "admin" || role === "superAdmin";

  useEffect(() => {
    setIsMentorChecked(userData?.isMentorVerified || false);
    setIsCourseChecked(userData?.isCourseVerified || false);
  }, [userData]);

  if (Loading) {
    return (
      <div className="flex h-screen items-center justify-center text-center text-lg">
        No data available
      </div>
    );
  }

  if (!userData) {
    return <div className="text-center text-lg">No data available</div>;
  }

  const mentorInfo = userData?.profile?.mentor || {};
  const userId = userData?._id || "";

  // Mentor fields (name/email/phone/position) can be stored in TWO places:
  //  1. profile.mentor.<field> - saved as an ARRAY (existing users who edited via Profile page)
  //  2. userData.<field> - saved as a plain STRING (users filled mentor at Step 7 signup)
  // This helper reads the profile array first (taking its first value), falling back
  // to the User-model string field, so both sources display correctly.
  const getMentorField = (profileValue, userValue) => {
    if (Array.isArray(profileValue) && profileValue.length > 0 && profileValue[0]) {
      return profileValue[0];
    }
    if (typeof profileValue === "string" && profileValue.trim() !== "") {
      return profileValue;
    }
    return userValue && userValue.trim() !== "" ? userValue : "NA";
  };

  const mentorName = getMentorField(mentorInfo.mentorName, userData?.mentorName);
  const mentorsEmail = getMentorField(mentorInfo.mentorsEmail, userData?.mentorsEmail);
  const mentorsPhoneNumber = getMentorField(
    mentorInfo.mentorsPhoneNumber,
    userData?.mentorsPhoneNumber,
  );
  const mentorPosition = getMentorField(mentorInfo.mentorPosition, "");

  const updateVerification = async (type) => {
    const isMentor = type === "mentor";

    const data = isMentor
      ? { userId, adminId, isMentorVerified: !isMentorChecked }
      : { userId, adminId, isCourseVerified: !isCourseChecked };

    const apiEndpoint = isMentor
      ? configuration.apis.mentorVerify
      : configuration.apis.courseVerify;

    if (isMentor) {
      setIsMentorChecked((prev) => !prev);
    } else {
      setIsCourseChecked((prev) => !prev);
    }

    try {
      const res = await authorizedPost(apiEndpoint, data, config);
      const response = res?.data;

      if (response?.status) {
        sessionStorage.setItem("matrimonyNeedsRefetch", "true");

        queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });

        successNotify(response?.message || "Status updated successfully");
      } else {
        if (isMentor) {
          setIsMentorChecked((prev) => !prev);
        } else {
          setIsCourseChecked((prev) => !prev);
        }
        errorNotify(response?.message || "Failed to update status");
      }
    } catch (error) {
      if (isMentor) {
        setIsMentorChecked((prev) => !prev);
      } else {
        setIsCourseChecked((prev) => !prev);
      }
      errorNotify(error?.response?.data?.message || "Server Error");
    }
  };

  return (
    <div className="font-jost mx-auto p-6">
      <div className="mb-2 bg-white px-4">
        <h3 className="mb-2 text-xl text-textYellow">Mentor Information</h3>
        <div className="flex flex-col border-b pb-4 md:flex-row md:flex-wrap">
          <p className="mb-2 w-full md:w-1/2">
            <span className="font-medium">Mentor Name:</span>{" "}
            {mentorName}
          </p>
          <p className="mb-2 w-full break-all md:w-1/2">
            <span className="font-medium">Mentor Email:</span>{" "}
            {mentorsEmail}
          </p>
          <p className="mb-2 w-full md:w-1/2">
            <span className="font-medium">Mentor Phone:</span>{" "}
            {mentorsPhoneNumber}
          </p>
          <p className="mb-2 w-full md:w-1/2">
            <span className="font-medium">Position:</span>{" "}
            {mentorPosition}
          </p>
        </div>

        <div className="mt-4 flex flex-col space-y-4 md:flex-row md:space-x-12 md:space-y-0">
          <div className="flex flex-1 items-center space-x-3 rounded-lg p-3">
            <input
              type="checkbox"
              id="mentorVerified"
              checked={isMentorChecked}
              onChange={() => canManage && updateVerification("mentor")}
              disabled={!canManage}
              className="h-5 w-5 cursor-pointer accent-textYellow disabled:cursor-not-allowed disabled:opacity-60"
            />
            <label
              htmlFor="mentorVerified"
              className={`font-medium ${
                isMentorChecked ? "text-green-600" : "text-gray-700"
              }`}
            >
              {isMentorChecked ? "Mentor Verified " : "Verify Mentor"}
            </label>
          </div>

          <div className="flex flex-1 items-center space-x-3 rounded-lg p-3">
            <input
              type="checkbox"
              id="courseVerified"
              checked={isCourseChecked}
              onChange={() => canManage && updateVerification("course")}
              disabled={!canManage}
              className="h-5 w-5 cursor-pointer accent-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
            />
            <label
              htmlFor="courseVerified"
              className={`font-medium ${
                isCourseChecked ? "text-blue-600" : "text-gray-700"
              }`}
            >
              {isCourseChecked ? "Course Attended " : "Mark Course Attended"}
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorDetails;